import { useMutation, useQueryClient } from "@tanstack/react-query";

import { translate } from "app/filters/i18nFilter";
import { getQueryKey } from "app/modules/api-client";
import { SHARE_OF_VOICE_QUERY_KEY } from "app/pages/activation/components/HomeWithData/ShareData/hooks/useShareOfVoiceWidgetQuery";
import { TRAFFIC_QUERY_KEY } from "app/pages/activation/components/HomeWithData/ShareData/hooks/useTrafficWidgetQuery";
import { COMMON_API_ERROR_MESSAGE } from "app/pages/activation/constants/texts";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { ACTIVATION_METADATA } from "app/pages/activation/queries/data/useActivationMetadataQuery";
import { ACTIVATION_OBJECTS_QUERY_KEY } from "app/pages/activation/queries/data/useActivationSetListQuery";
import { SHORT_ACTIVATION_OBJECTS_QUERY_KEY } from "app/pages/activation/queries/data/useShortActivationSetListQuery";
import { PRIORITY_ALERTS_KEY } from "app/pages/activation/queries/homeWithData/usePriorityAlertsQuery";
import { ACTIVATION_HOME_ROUTE } from "app/pages/activation/routing/constants";
import { createActivationApi } from "app/pages/activation/services/api";
import { activationTracking } from "app/pages/activation/services/tracking";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useLastSelectedSet } from "../../hooks/home/useLastSelectedSet";

const useDeleteActivation = (activationId: string) => {
    const { showErrorToast } = useToasts();
    const { getLastSelectedActivation, removeLastSelectedActivation } = useLastSelectedSet();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, shouldRemoveKWList }: { id: string; shouldRemoveKWList: boolean }) => {
            const api = createActivationApi();

            return api.activation.delete(id, shouldRemoveKWList);
        },
        onSuccess(_data, { shouldRemoveKWList }) {
            const lastOpenDate: string = PreferencesService.get(`${activationId}_open_alert`);

            const lastSelectedSet = getLastSelectedActivation();

            if (lastSelectedSet === activationId) {
                removeLastSelectedActivation();
            }

            if (lastOpenDate) {
                void PreferencesService.remove([`${activationId}_open_alert`]);
            }

            void queryClient.invalidateQueries({
                queryKey: getQueryKey({
                    tag: "Activation",
                    methodName: "userdataActivationDetail",
                    args: [activationId],
                }),
                refetchType: "none",
            });
            void queryClient.invalidateQueries({ queryKey: [ACTIVATION_OBJECTS_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: [SHORT_ACTIVATION_OBJECTS_QUERY_KEY] });
            void queryClient.resetQueries({ queryKey: [ACTIVATION_METADATA] });
            void queryClient.removeQueries({ queryKey: [PRIORITY_ALERTS_KEY, activationId] });
            void queryClient.removeQueries({ queryKey: [TRAFFIC_QUERY_KEY, activationId] });
            void queryClient.removeQueries({ queryKey: [SHARE_OF_VOICE_QUERY_KEY, activationId] });

            activationTracking.settings.trackDeleteActivation(shouldRemoveKWList);

            void proNavigator.go(ACTIVATION_HOME_ROUTE);
        },
        onError(error: Error) {
            showErrorToast(translate(COMMON_API_ERROR_MESSAGE, { error: error?.message }));
        },
    });
};
export default useDeleteActivation;
